import React from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { FlowDesignerSearchParams } from '../../router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError, isExpectedClientApiError } from '../../lib/api-error';
import { extractUuid } from '../../lib/prefixed-ids';
import { DocsAnnotation, useToast } from '@chatsdet/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useEligibilityModel } from '../../hooks/useEligibilityModel';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import type { WorkflowEdge, WorkflowNode } from '../../types/workflow';
import { useStableNodesState, type QuickCreateTarget } from './FlowDesigner/helpers';
import { useWorkflowExecution } from '../../hooks/useWorkflowExecution';
import { useWorkflowBuilderStatus } from '../../hooks/useWorkflowBuilderStatus';
import { useWorkflowMutations } from '../../hooks/useWorkflowMutations';
import { useCacheOptimizerStream } from '../../hooks/useCacheOptimizerStream';
import { useHints } from '../../hooks/useHints';
import { type WorkflowStatus, DEFAULT_WORKFLOW_NAME } from './FlowDesigner/constants';
import { isTempNodeId, formatUpdatedAt, getDefaultNodeData } from './FlowDesigner/utils';
import { useLifecycleControls } from './FlowDesigner/hooks/useLifecycleControls';
import { useAutobuildStartMutation } from './FlowDesigner/hooks/useFlowAutobuild';
import type { AutobuildSubmitPayload } from './FlowDesigner/StarterPicker';
import { useUndoHistory, type AttachedEffect } from './FlowDesigner/hooks/useUndoHistory';
import { useRevertProposal } from './FlowDesigner/proposals/useWorkflowProposals';
import {
  useWorkflowSnapshots,
  useWorkflowRevisions,
  useWorkflowSnapshot,
  useSaveVersion,
  useWorkflowRevision,
  useRestoreWorkflowRevision,
  useDraftSnapshot,
  useRestoreDraftSnapshot,
} from './FlowDesigner/hooks/useWorkflowSnapshots';
import type { VersionSelection } from './FlowDesigner/VersionDropdown';
import { VersionNameDialog } from './FlowDesigner/VersionNameDialog';
import { nodeLabel } from './FlowDesigner/utils/nodeLabel';
import {
  useSetWorkflowStatus,
  useSetNodeDeactivated,
} from './FlowDesigner/hooks/useWorkflowEvents';
import {
  isBrowserViewportPreset,
  type BrowserViewportPreset,
  type WorkflowManagementStatus,
} from '@chatsdet/types';
import { useFlowValidation } from './FlowDesigner/validation/useFlowValidation';
import { useSeedNodeOutputs } from './FlowDesigner/hooks/useSeedNodeOutputs';
import { useCallFlowNodeOutputs } from './FlowDesigner/hooks/useCallFlowNodeOutputs';
import {
  useWorkflowVariables,
  type EnvVarDefinition,
} from './FlowDesigner/hooks/useWorkflowVariables';
import { fetchOrgPropertyById } from '../../hooks/useOrgProperty';
import { WorkflowVariablesProvider } from './FlowDesigner/context/WorkflowVariablesContext';
import { FlowOverlayPortalProvider } from './FlowDesigner/context/FlowOverlayPortalContext';
import { useOrgCredentials } from '../../hooks/useOrgCredentials';
import { useSandboxV2Applications } from '../../features/sandboxes/hooks/useSandboxesV2';
import { useOrgCredentialTemplates } from '../../hooks/useCredentialTemplates';
import { useParentSeedOutputs } from '../../hooks/useParentSeedOutputs';
import { useApiRequestList } from '../../features/api-catalog/api';
import { useWorkflowOpenIssues } from '../../hooks/useWorkflowOpenIssues';
import { useAvailableSandboxActions } from '../../hooks/useAvailableSandboxActions';
import type { NodeConfigPanelHandle } from '../components/NodeConfigPanel';

import { nodeTypes, type SidebarTab, type FlowView } from './FlowDesigner/types';
import { useNodeGraphMaps } from './FlowDesigner/hooks/useNodeGraphMaps';
import { useSandboxV2DesignerTarget } from './FlowDesigner/hooks/useSandboxV2DesignerTarget';
import { useWorkflowNaming } from './FlowDesigner/hooks/useWorkflowNaming';
import { useWorkflowRun } from './FlowDesigner/hooks/useWorkflowRun';
import { useCanvasNodes } from './FlowDesigner/hooks/useCanvasNodes';
import { usePlaybookCache } from './FlowDesigner/hooks/usePlaybookCache';
import { useCanvasHydration } from './FlowDesigner/hooks/useCanvasHydration';
import { useNodeCrud } from './FlowDesigner/hooks/useNodeCrud';
import { useClipboard } from './FlowDesigner/hooks/useClipboard';
import { useRunExecution } from './FlowDesigner/hooks/useRunExecution';
import {
  useCacheConvergenceMutation,
  type ActiveOptimization,
} from './FlowDesigner/hooks/useCacheConvergence';
import { useCachePinMutation } from './FlowDesigner/hooks/useCachePin';
import { useReviewWorkflow } from './FlowDesigner/hooks/useReviewWorkflow';
import { useExplorationBanner } from './FlowDesigner/hooks/useExplorationBanner';
import { useWorkflowForkLineage } from './FlowDesigner/hooks/useWorkflowForkLineage';
import { useResizableSidebarWidth } from './FlowDesigner/hooks/useResizableSidebarWidth';
import {
  FlowDesignerCanvas,
  FlowDesignerSidebar,
  FlowDesignerDialogs,
} from './FlowDesigner/components';
import { WorkflowRunConfigModal } from './FlowDesigner/components/WorkflowRunConfigModal';
import { resolveSandboxWarmTarget } from './FlowDesigner/sandboxTarget';
import { useSandboxEnvironment } from '../../hooks/useSandboxEnvironment';
import { useApiOnlyToggle } from './FlowDesigner/api/hooks';
import { FlowDesignerProposals } from './FlowDesigner/proposals/FlowDesignerProposals';
import { ProposalReviewView } from './FlowDesigner/proposals/ProposalReviewView';
import {
  useSuggestModeController,
  useSuggestModeWiring,
} from './FlowDesigner/proposals/useSuggestModeWiring';
import { apiSequenceStepsNeedCatalog } from './FlowDesigner/deriveApiSequenceStepLabels';
import { fetchWorkflowValidationList } from './FlowDesigner/workflowValidationList';

export const FlowDesigner: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, switchOrg, isSwitchingOrg } = useAuth();
  const currentOrgId = user?.orgId ?? null;

  // URL params
  const params = useParams({ strict: false }) as { workflowId?: string };
  const workflowId = extractUuid(params.workflowId) ?? null;

  // Feature flags
  const hasApiCatalog = true; // GA — flag deprecated
  const hasCacheOptimizer = !!user?.features?.cacheOptimizer;
  const isSuperadmin = !!user?.isSuperadmin;
  // MCP nodes are gated on the org's mcpTesting feature (the node targets an
  // org property with type 'mcp').
  const hasMcp = !!user?.features?.mcpTesting;
  // Flow Autobuild (spec -> flow pipeline) is the same feature flag as the
  // Spec Review exploratory-test flavor — see WEB_REVIEW_FLAVORS.spec.featureKey.
  const autobuildEnabled = !!user?.features?.specReview;
  // Per-org eligibility model: when ON, suite inclusion is derived (approved +
  // ever-passed) and pausing is a standalone toggle. When OFF, the legacy
  // promotion model (Approve → Promote / Force Promote) is used.
  const eligibilityModel = useEligibilityModel();
  // Sandbox actions are gated by configuration, not a flag: the node type only
  // shows once the org has a Sandboxes v2 application.
  const { data: sandboxApplications } = useSandboxV2Applications();
  const hasSandboxActions = (sandboxApplications?.length ?? 0) > 0;

  // Hints
  const hintData = useHints('flow-designer');

  // Core state — default to run tab when runId is in URL
  const routeSearch = useSearch({ from: '/flows/$workflowId' }) as {
    runId?: string;
    proposalId?: string;
    revision?: number;
  };
  const [activeSidebarTab, setActiveSidebarTab] = React.useState<SidebarTab>(
    routeSearch.runId ? 'run' : 'config'
  );
  const lastNonConfigTabRef = React.useRef<SidebarTab>('run');
  const { nodes, setNodes, getNodes } = useStableNodesState();
  const [edges, setEdges] = React.useState<WorkflowEdge[]>([]);
  const edgesRef = React.useRef<WorkflowEdge[]>([]);
  React.useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);
  const getEdges = React.useCallback(() => edgesRef.current, []);
  const [selectedNodeForConfig, setSelectedNodeForConfig] = React.useState<WorkflowNode | null>(
    null
  );
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = React.useState(false);
  const [workflowToArchive, setWorkflowToArchive] = React.useState<{
    id: string;
    name: string;
  } | null>(null);
  const [_isGeneratingName, setIsGeneratingName] = React.useState(false);
  const [generatedName, setGeneratedName] = React.useState<string | null>(null);
  const [previewingVersion, setPreviewingVersion] = React.useState<VersionSelection | null>(() =>
    routeSearch.revision && routeSearch.revision > 0
      ? { type: 'revision', revision: routeSearch.revision }
      : null
  );
  const routeRevisionRef = React.useRef(routeSearch.revision);
  const nodeConfigPanelRef = React.useRef<NodeConfigPanelHandle | null>(null);
  const autoFocusedWorkflowRef = React.useRef<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = React.useState<string | null>(null);
  const [edgeMenuPosition, setEdgeMenuPosition] = React.useState<{ x: number; y: number } | null>(
    null
  );
  const [isInsertingNode, setIsInsertingNode] = React.useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [isScenarioConfigOpen, setIsScenarioConfigOpen] = React.useState(false);
  const [isPlaybookOpen, setIsPlaybookOpen] = React.useState(false);
  const [playbookScrollToNodeId, setPlaybookScrollToNodeId] = React.useState<string | null>(null);
  const [cacheDebugRunId, setCacheDebugRunId] = React.useState<string | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = React.useState<Set<string>>(new Set());
  const [wrongOrgInfo, setWrongOrgInfo] = React.useState<{ orgId: string; orgName: string } | null>(
    null
  );
  const [_cachePopover, setCachePopover] = React.useState<{
    nodeId: string;
    position: { x: number; y: number };
  } | null>(null);
  const [historicalFailedNodeId, setHistoricalFailedNodeId] = React.useState<string | null>(null);
  // Promotion model (eligibility flag OFF) only.
  const [isForcePromoteDialogOpen, setForcePromoteDialogOpen] = React.useState(false);
  // Member-visible flow optimization confirmation dialog.
  const [isOptimizeDialogOpen, setOptimizeDialogOpen] = React.useState(false);
  const [isApproveDialogOpen, setApproveDialogOpen] = React.useState(false);
  const draftStateRef = React.useRef<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] } | null>(null);
  const [flowView, setFlowView] = React.useState<FlowView>('browser');
  const [reviewingProposalId, setReviewingProposalId] = React.useState<string | null>(
    routeSearch.proposalId ?? null
  );
  const sidebarResize = useResizableSidebarWidth();

  // The canvas column, published so sidebar-mounted slide-up overlays can
  // portal into it instead of being clipped by the sidebar's own `relative`
  // containing block. State (not a ref) so consumers re-render once it mounts.
  const [overlayHost, setOverlayHost] = React.useState<HTMLDivElement | null>(null);

  // Deep-link: open the proposal review when `?proposalId=` is present (e.g. the
  // "Proposed fix" callout on a run). Runs on later search changes too, since the
  // run detail lives on this same route.
  React.useEffect(() => {
    if (routeSearch.proposalId) {
      setReviewingProposalId(routeSearch.proposalId);
    }
  }, [routeSearch.proposalId]);

  // Closing the review clears local state and strips the deep-link param so the
  // effect above doesn't immediately reopen it.
  const exitProposalReview = React.useCallback(() => {
    setReviewingProposalId(null);
    if (routeSearch.proposalId) {
      navigate({
        to: '/flows/$workflowId',
        params: { workflowId },
        search: (prev: FlowDesignerSearchParams) => ({ ...prev, proposalId: undefined }),
        replace: true,
      });
    }
  }, [navigate, routeSearch.proposalId, workflowId]);

  const clearEdgeSelection = React.useCallback(() => {
    setSelectedEdgeId(null);
    setEdgeMenuPosition(null);
  }, []);

  // Credentials
  const orgCredentialsQ = useOrgCredentials();
  const { credentials, credentialPropertyMap, credentialPropertyIdMap } = orgCredentialsQ;
  const orgTemplatesQ = useOrgCredentialTemplates();
  const templateNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const t of orgTemplatesQ.data ?? []) m.set(t.id, t.name);
    return m;
  }, [orgTemplatesQ.data]);
  // API catalog
  const shouldLoadApiCatalogLabels = React.useMemo(
    () => apiSequenceStepsNeedCatalog(nodes),
    [nodes]
  );
  const apiRequestListQ = useApiRequestList({
    pageSize: 50,
    enabled: hasApiCatalog && shouldLoadApiCatalogLabels,
  });
  const apiRequests = apiRequestListQ.data?.data ?? [];

  // Workflow detail query
  const workflowDetailQ = useQuery({
    queryKey: ['workflow-detail', workflowId],
    queryFn: async () => {
      if (!workflowId) return null;
      const r = await fetch(`${API_URL}/workflows/${workflowId}`, { credentials: 'include' });
      if (!r.ok) {
        const errorData = await r.json().catch(() => ({}));
        if (errorData.error === 'WORKFLOW_WRONG_ORG') {
          setWrongOrgInfo({ orgId: errorData.targetOrgId, orgName: errorData.targetOrgName });
          throw new Error('WORKFLOW_WRONG_ORG');
        }
        // 404 is expected when switching orgs in another tab — return null
        // instead of throwing to avoid noisy Sentry errors
        return null;
      }
      const data = await r.json();
      return data.workflow;
    },
    enabled: !!workflowId,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    // Poll while a cache-convergence run is actively in flight so the
    // optimizing banner and menu item stay live without a manual refresh.
    refetchInterval: (query) => (query.state.data?.optimization ? 15_000 : false),
  });

  const workflowData = workflowDetailQ.data;
  const workflowNotFound = Boolean(
    workflowId && !workflowDetailQ.isLoading && workflowData === null
  );
  const workflowName = workflowData?.name ?? DEFAULT_WORKFLOW_NAME;
  const workflowDescription = workflowData?.description ?? null;
  const workflowPreflight = workflowData?.preflight ?? null;
  const workflowCleanup = workflowData?.cleanup ?? null;
  const workflowRunAgentically = workflowData?.runAgentically ?? false;
  const workflowSeedRunOncePerSuite = workflowData?.seedRunOncePerSuite ?? false;
  const workflowStatus = (workflowData?.status as WorkflowStatus) ?? 'draft';
  const workflowHealth = workflowData?.health as string | undefined;
  const workflowManagementStatus = workflowData?.managementStatus as
    | WorkflowManagementStatus
    | undefined;
  const workflowFlowType = workflowData?.flowType ?? 'standard';
  const activeOptimization: ActiveOptimization = workflowData?.optimization ?? null;
  // The quick-create callbacks below are intentionally identity-stable (empty
  // dep arrays, reading `onCreateNodeRef.current`). `workflowFlowType` resolves
  // asynchronously from the workflow query, so reading it directly from those
  // closures would freeze it at the first render's 'standard' fallback and give
  // new wait nodes the wrong default. Mirror the `edgesRef` pattern instead.
  const workflowFlowTypeRef = React.useRef(workflowFlowType);
  React.useEffect(() => {
    workflowFlowTypeRef.current = workflowFlowType;
  }, [workflowFlowType]);
  const isWorkflowLoading = Boolean(workflowId && workflowDetailQ.isLoading);
  const workflowIsApproved = workflowStatus === 'approved';
  // Approval is a spec-review judgment — any settled draft with content can be
  // approved, no passing run required. System states (generating/building/…)
  // and empty canvases are not approvable.
  const workflowIsApprovable =
    workflowId !== null && workflowStatus === 'draft' && nodes.length > 0;
  const workflowFirstPassedAt = (workflowData?.firstPassedAt ?? null) as string | null;
  const workflowPaused = (workflowData?.pausedAt ?? null) != null;
  // Promotion model (eligibility flag OFF) inputs.
  const workflowReadyToPromote = Boolean(workflowData?.readyToPromote);
  const cachePinnedAt = (workflowData?.cachePinnedAt ?? null) as string | null;
  const effectivePinned = Boolean(workflowData?.effectivePinned);
  const isShadowFork = Boolean(workflowData?.shadowAt);
  const workflowPromotion = (workflowData?.promotion ?? null) as 'promoted' | 'paused' | null;
  // Fork lineage (present only when this flow was forked from another).
  const forkedFromWorkflowId = (workflowData?.forkedFromWorkflowId ?? null) as string | null;
  const forkedFromName = (workflowData?.forkedFromName ?? null) as string | null;
  const forkLineage = useWorkflowForkLineage({
    workflowId,
    forkedFromWorkflowId,
    forkedFromName,
    isSuperadmin,
  });
  const workflowDetailRefetch = workflowDetailQ.refetch;

  // Auto-switch to API view on initial load when the workflow is either
  // explicitly flagged apiOnly OR composed entirely of server-side
  // (api_sequence/sandbox_action) and end nodes (mirrors the backend's
  // isApiOnlySegment detection in
  // apps/api/src/workflows/helpers/api-only-detection.ts). Without this, a flow
  // built from only server-side nodes lands in browser view, where the runs
  // list filters out isApiOnly runs and shows nothing.
  const apiOnlyInitRef = React.useRef(false);
  React.useEffect(() => {
    if (apiOnlyInitRef.current) return;
    const nodes = (workflowData?.nodes ?? []) as Array<{ nodeType?: string }>;
    const allApi =
      nodes.length > 0 &&
      nodes.every(
        (n) =>
          n.nodeType === 'api_sequence' || n.nodeType === 'sandbox_action' || n.nodeType === 'end'
      );
    if (workflowData?.apiOnly || allApi) {
      apiOnlyInitRef.current = true;
      setFlowView('api');
    }
  }, [workflowData?.apiOnly, workflowData?.nodes]);

  // Derive a propertyId for operations that need one (e.g. creating a blank
  // API sequence) — prefer a login credential already used by the workflow,
  // fall back to the first org credential's property.
  const derivedWorkflowPropertyId = React.useMemo<string | null>(() => {
    const nodes = (workflowData?.nodes ?? []) as Array<{
      data?: { credentialId?: string | null };
    }>;
    for (const node of nodes) {
      const credId = node.data?.credentialId;
      if (credId) {
        const propId = credentialPropertyIdMap.get(credId);
        if (propId) return propId;
      }
    }
    return credentials[0]?.propertyId ?? null;
  }, [workflowData?.nodes, credentialPropertyIdMap, credentials]);

  // The workflow's property declares environment variables (injected at run time
  // as $var.ENV.<name>). Fetch them so they appear in the macro/variable typeahead.
  const envVarPropertyQ = useQuery({
    queryKey: ['org-property', derivedWorkflowPropertyId],
    queryFn: () =>
      fetchOrgPropertyById<{ id: string; envVarDefinitions?: EnvVarDefinition[] }>(
        derivedWorkflowPropertyId!
      ),
    enabled: !!derivedWorkflowPropertyId,
    staleTime: 60_000,
  });
  const envVarDefinitions = envVarPropertyQ.data?.envVarDefinitions;

  // API-only toggle with optimistic local state
  const apiOnlyMutation = useApiOnlyToggle(workflowId);
  const [localApiOnly, setLocalApiOnly] = React.useState(false);
  React.useEffect(() => {
    setLocalApiOnly(workflowData?.apiOnly ?? false);
  }, [workflowData?.apiOnly]);
  const handleApiOnlyToggle = React.useCallback(
    (apiOnly: boolean) => {
      setLocalApiOnly(apiOnly);
      if (apiOnly) setFlowView('api'); // auto-switch to API view
      apiOnlyMutation.mutate(apiOnly, {
        onSuccess: () =>
          toast.success(apiOnly ? 'API-only mode enabled' : 'API-only mode disabled'),
        onError: (err) => {
          setLocalApiOnly(!apiOnly);
          toast.error(`Failed: ${err.message}`);
        },
      });
    },
    [apiOnlyMutation, toast]
  );

  // For seed workflows, expose the workflow's own seed outputs (with credential info)
  // so credential variables like $var.credential.username appear in autocomplete.
  // For teardown workflows, fetch the parent seed workflow's outputs.
  const { data: parentSeedData } = useParentSeedOutputs(workflowId, workflowData?.flowType);
  const selfSeedOutputs = React.useMemo(() => {
    if (workflowData?.flowType === 'teardown') {
      // Show all parent seed outputs (not just credential ones) as available variables
      const outputs = parentSeedData?.outputs;
      return outputs && outputs.length > 0 ? outputs : undefined;
    }
    if (workflowData?.flowType !== 'seed') return undefined;
    const outputs = workflowData?.seedOutputsJson as
      | Array<{
          dataKey: string;
          name: string;
          credentialOutput?: { fieldMapping: Record<string, string> };
        }>
      | undefined;
    if (!outputs?.length) return undefined;
    // Only include outputs that have credential provisioning
    const withCreds = outputs.filter((o) => o.credentialOutput?.fieldMapping);
    return withCreds.length > 0 ? withCreds : undefined;
  }, [workflowData?.flowType, workflowData?.seedOutputsJson, parentSeedData?.outputs]);

  const { seedOutputsByWorkflowId, transitiveSeedOutputs } = useSeedNodeOutputs(nodes);
  const { callFlowOutputsByFlowId } = useCallFlowNodeOutputs(nodes);
  const workflowInputs = workflowData?.inputsJson as
    | import('@chatsdet/types').WorkflowInputDefinition[]
    | undefined;
  const workflowScenarios = workflowData?.scenariosJson as
    | import('@chatsdet/types').WorkflowScenario[]
    | undefined;

  const qc = useQueryClient();

  // Manually clear `degraded` health (clean slate) so the flow runs in suites again.
  const reEnableMutation = useMutation({
    mutationFn: async () => {
      if (!workflowId) return;
      const r = await fetch(`${API_URL}/workflows/${workflowId}/re-enable`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      toast.success('Flow re-enabled — it will run in the next suite');
    },
    onError: () => toast.error('Could not re-enable the flow'),
  });

  // Manual lifecycle axis (active/paused/blocked/under_construction/deprecated),
  // distinct from approval/health. Wired into the toolbar's WorkflowStatusControl.
  const setWorkflowStatusMutation = useSetWorkflowStatus(workflowId ?? '');
  const handleChangeManagementStatus = React.useCallback(
    (input: Parameters<ReturnType<typeof useSetWorkflowStatus>['mutate']>[0]) => {
      setWorkflowStatusMutation.mutate(input, {
        onSuccess: () => toast.success('Flow status updated'),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Could not update flow status'),
      });
    },
    [setWorkflowStatusMutation, toast]
  );

  const saveInputsMutation = useMutation({
    mutationFn: async (inputs: import('@chatsdet/types').WorkflowInputDefinition[]) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/scenario-schema`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ inputs }),
      });
      if (!r.ok) throw new Error('Failed to save workflow inputs');
      return r.json();
    },
    onMutate: async (inputs) => {
      // Optimistic update: immediately reflect the new inputs in the UI
      if (!workflowId) return;
      await qc.cancelQueries({ queryKey: ['workflow-detail', workflowId] });
      qc.setQueryData(['workflow-detail', workflowId], (old: any) =>
        old ? { ...old, inputsJson: inputs } : old
      );
    },
    onSuccess: () => toast.success('Schema saved'),
    onError: () => toast.error('Could not save schema'),
    onSettled: () => {
      if (workflowId) qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });

  const saveScenariosMutation = useMutation({
    mutationFn: async (scenarios: import('@chatsdet/types').WorkflowScenario[]) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/scenarios/import`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ mode: 'replace', scenarios }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to save workflow scenarios');
      return r.json();
    },
    onMutate: async (scenarios) => {
      if (!workflowId) return;
      await qc.cancelQueries({ queryKey: ['workflow-detail', workflowId] });
      qc.setQueryData(['workflow-detail', workflowId], (old: any) =>
        old ? { ...old, scenariosJson: scenarios } : old
      );
    },
    onSettled: () => {
      if (workflowId) qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });

  const workflowVariables = useWorkflowVariables(
    nodes,
    seedOutputsByWorkflowId,
    selfSeedOutputs,
    transitiveSeedOutputs,
    workflowInputs,
    envVarDefinitions,
    callFlowOutputsByFlowId
  );
  // Validation lives on the server now (apps/api/src/workflows/validation/).
  // The hook is a pure state container for issues returned by /execute.
  const flowValidation = useFlowValidation();

  // Version history
  const snapshotsQ = useWorkflowSnapshots(workflowId);
  const revisionsQ = useWorkflowRevisions(workflowId);
  const draftSnapshots = snapshotsQ.data?.draftSnapshots ?? [];
  const publishedPreviewVersion =
    previewingVersion?.type === 'snapshot' ? previewingVersion.version : null;
  const revisionPreviewNumber =
    previewingVersion?.type === 'revision' ? previewingVersion.revision : null;
  const draftSnapshotPreviewVersion =
    previewingVersion?.type === 'draft-snapshot' ? previewingVersion.version : null;
  const previewSnapshotQ = useWorkflowSnapshot(workflowId, publishedPreviewVersion);
  const previewRevisionQ = useWorkflowRevision(workflowId, revisionPreviewNumber);
  const previewDraftSnapshotQ = useDraftSnapshot(workflowId, draftSnapshotPreviewVersion);
  const isPreviewingHistory = previewingVersion !== null;
  const workflowViewportSizeForRun: BrowserViewportPreset | null = (() => {
    if (previewingVersion?.type === 'revision') {
      const value = previewRevisionQ.data?.snapshotJson.viewportSize;
      return isBrowserViewportPreset(value) ? value : null;
    }
    if (previewingVersion?.type === 'snapshot') {
      const value = previewSnapshotQ.data?.snapshotJson.viewportSize;
      return isBrowserViewportPreset(value) ? value : null;
    }
    if (previewingVersion?.type === 'draft-snapshot') {
      const value = previewDraftSnapshotQ.data?.snapshotJson.viewportSize;
      return isBrowserViewportPreset(value) ? value : null;
    }
    const value = workflowData?.viewportSize;
    return isBrowserViewportPreset(value) ? value : null;
  })();
  const saveVersionMutation = useSaveVersion(workflowId);
  const restoreRevisionMutation = useRestoreWorkflowRevision(workflowId);
  const restoreDraftSnapshotMutation = useRestoreDraftSnapshot(workflowId);

  React.useEffect(() => {
    if (routeSearch.revision === routeRevisionRef.current) return;
    routeRevisionRef.current = routeSearch.revision;
    setPreviewingVersion(
      routeSearch.revision && routeSearch.revision > 0
        ? { type: 'revision', revision: routeSearch.revision }
        : null
    );
  }, [routeSearch.revision]);

  // Breadcrumbs
  const breadcrumbs = React.useMemo(
    () => [
      { label: 'Home', onPress: () => navigate({ to: '/' }) },
      { label: 'Flows', onPress: () => navigate({ to: '/flows' }) },
      ...(workflowId
        ? // The current page: no onPress, so Breadcrumb renders it as the truncating current crumb.
          [{ label: workflowName }]
        : [{ label: 'New Flow' }]),
    ],
    [navigate, workflowId, workflowName]
  );
  useBreadcrumbs(breadcrumbs);

  // Sidebar tab management
  const selectSidebarTab = React.useCallback((tab: SidebarTab) => {
    if (tab !== 'config') lastNonConfigTabRef.current = tab;
    setActiveSidebarTab(tab);
  }, []);

  const exitConfigTab = React.useCallback(() => {
    setActiveSidebarTab(lastNonConfigTabRef.current ?? 'run');
  }, []);

  // Auto-save dirty node config
  const autoSaveIfDirty = React.useCallback(async () => {
    const panel = nodeConfigPanelRef.current;
    if (panel && panel.isDirty && !panel.isSaving) {
      setIsSaving(true);
      try {
        await panel.save();
      } finally {
        setIsSaving(false);
      }
    }
  }, []);

  const handleCloseConfigPanel = React.useCallback(async () => {
    await autoSaveIfDirty();
    setSelectedNodeForConfig(null);
    exitConfigTab();
  }, [exitConfigTab, autoSaveIfDirty]);

  const handleDeselectNode = React.useCallback(async () => {
    await autoSaveIfDirty();
    setSelectedNodeForConfig(null);
    setSelectedNodeIds(new Set());
  }, [autoSaveIfDirty]);

  // Version history handlers
  const handleVersionSelect = React.useCallback(
    (selection: VersionSelection) => {
      if (selection.type === 'current') {
        if (draftStateRef.current) {
          setNodes(draftStateRef.current.nodes);
          setEdges(draftStateRef.current.edges);
          draftStateRef.current = null;
        }
        setPreviewingVersion(null);
      } else {
        if (previewingVersion === null) {
          draftStateRef.current = { nodes: getNodes(), edges };
        }
        setPreviewingVersion(selection);
      }
    },
    [previewingVersion, setNodes, setEdges, getNodes, edges]
  );

  const handleBackToCurrent = React.useCallback(() => {
    handleVersionSelect({ type: 'current' });
  }, [handleVersionSelect]);

  // Version name dialog state — true while the "Save Revision..." dialog is open.
  const [isSaveVersionDialogOpen, setIsSaveVersionDialogOpen] = React.useState(false);

  const handleSaveVersion = React.useCallback(() => {
    setIsSaveVersionDialogOpen(true);
  }, []);

  const handleVersionDialogSubmit = React.useCallback(
    (name: string | null) => {
      saveVersionMutation.mutate(name ?? undefined, {
        onSuccess: () => toast.success('Revision saved'),
        onError: () => toast.error('Failed to save version', { reportError: false }),
      });
    },
    [saveVersionMutation, toast]
  );

  const goToFlows = React.useCallback(
    (overrides?: { workflowId?: string | null }, options?: { replace?: boolean }) => {
      const resolvedWorkflowId = overrides?.workflowId ?? workflowId ?? null;
      if (resolvedWorkflowId) {
        navigate({
          to: '/flows/$workflowId',
          params: { workflowId: resolvedWorkflowId },
          replace: options?.replace,
        });
      } else {
        navigate({ to: '/flows', replace: options?.replace });
      }
    },
    [navigate, workflowId]
  );

  // Escape key handler
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Workflow execution
  const {
    isRunning,
    isReconnecting,
    runId,
    events,
    currentStepId,
    nodeStatuses,
    screenshotItems,
    startExecution,
    stopExecution,
    reconnectToRun,
    checkForActiveRun,
  } = useWorkflowExecution(workflowId);

  // Exploration banner (shows when many nodes are uncached)
  const explorationBanner = useExplorationBanner({ nodes, isRunning });

  // ── Cache optimizer live progress (canvas chips) ──────────────────────
  // jobId comes from polling optimizer-status for auto runs (where the web
  // never receives a jobId).
  const [optimizerJobId, setOptimizerJobId] = React.useState<string | null>(null);
  const optimizerStatusQ = useQuery({
    queryKey: ['optimizer-status', workflowId],
    enabled: hasCacheOptimizer && !!workflowId,
    refetchInterval: hasCacheOptimizer ? 7000 : false,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/workflows/${workflowId}/optimizer-status`, {
        credentials: 'include',
      });
      if (!res.ok) return { active: false, jobId: null as string | null };
      return res.json() as Promise<{ active: boolean; jobId: string | null }>;
    },
  });
  React.useEffect(() => {
    const found = optimizerStatusQ.data?.jobId ?? null;
    // Adopt a newly-discovered job; never clear a finished job's chips here
    // (the durable result lives in the playbook view's cache row).
    if (found && found !== optimizerJobId) setOptimizerJobId(found);
  }, [optimizerStatusQ.data?.jobId, optimizerJobId]);
  // Poll promptly when a run finishes — an auto optimization may have just been
  // enqueued by the run-completion trigger.
  const prevIsRunningRef = React.useRef(isRunning);
  React.useEffect(() => {
    if (prevIsRunningRef.current && !isRunning) {
      const t = setTimeout(() => void optimizerStatusQ.refetch(), 1500);
      prevIsRunningRef.current = isRunning;
      return () => clearTimeout(t);
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, optimizerStatusQ]);
  const optimizerStream = useCacheOptimizerStream({ jobId: optimizerJobId });

  // ── Cache convergence agent ("Optimize" menu item; the resulting in-flight status
  // is visible to every org member
  // via `workflowData.optimization`) ──────────────────────────────────────
  const convergenceMutation = useCacheConvergenceMutation(workflowId);
  // `convergenceMutation.isPending` disables the menu item optimistically on
  // click, before the next workflow-detail refetch lands with `optimization`.
  const isConvergencePending = convergenceMutation.isPending || !!activeOptimization;

  // ── Manual cache pin/unpin ("Pin cache" / "Unpin cache" menu item) ──────
  const cachePinMutation = useCachePinMutation(workflowId);
  const handlePinToggle = async (pin: boolean) => {
    try {
      await cachePinMutation.mutateAsync(pin);
      toast.success(pin ? 'Cache pinned' : 'Cache unpinned');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : pin ? 'Failed to pin cache' : 'Failed to unpin cache'
      );
    }
  };

  // Builder status
  const {
    status: _builderStatus,
    streamId: builderStreamId,
    nodeCount: builderNodeCount,
    isGenerating,
    refetch: refetchBuilderStatus,
  } = useWorkflowBuilderStatus(workflowId);

  // Check for active run on mount
  React.useEffect(() => {
    if (workflowId && !isRunning) checkForActiveRun();
  }, [workflowId]);

  // Refresh workflow detail when a new run starts (may create a new draft snapshot)
  const prevRunIdRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (runId && workflowId && prevRunIdRef.current !== runId) {
      qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    }
    prevRunIdRef.current = runId ?? undefined;
  }, [runId, workflowId, qc]);

  // Mutations
  const {
    createWorkflowMutation,
    updateWorkflowMutation,
    archiveWorkflowMutation,
    createNodeMutation: realCreateNodeMutation,
    updateNodeMutation: realUpdateNodeMutation,
    deleteNodeMutation: realDeleteNodeMutation,
    bulkDeleteNodesMutation: realBulkDeleteNodesMutation,
    createEdgeMutation: realCreateEdgeMutation,
    deleteEdgeMutation: realDeleteEdgeMutation,
    insertNodeOnEdgeMutation: realInsertNodeOnEdgeMutation,
    approveWorkflowMutation,
    promoteWorkflowMutation,
    pauseWorkflowMutation,
    unpauseWorkflowMutation,
    syncDraftStateMutation: realSyncDraftStateMutation,
  } = useWorkflowMutations({
    currentOrgId,
    workflowId,
    onWorkflowCreated: (workflow) => goToFlows({ workflowId: workflow.id }),
    onWorkflowArchived: (archivedId) => {
      if (archivedId === workflowId) {
        goToFlows({ workflowId: null });
        setNodes([]);
        setEdges([]);
      }
    },
  });

  const {
    stagedMutations: {
      createNodeMutation,
      updateNodeMutation,
      deleteNodeMutation,
      bulkDeleteNodesMutation,
      createEdgeMutation,
      deleteEdgeMutation,
      insertNodeOnEdgeMutation,
      syncDraftStateMutation,
    },
    suggestingRef,
  } = useSuggestModeWiring({
    realMutations: {
      createNodeMutation: realCreateNodeMutation,
      updateNodeMutation: realUpdateNodeMutation,
      deleteNodeMutation: realDeleteNodeMutation,
      bulkDeleteNodesMutation: realBulkDeleteNodesMutation,
      createEdgeMutation: realCreateEdgeMutation,
      deleteEdgeMutation: realDeleteEdgeMutation,
      insertNodeOnEdgeMutation: realInsertNodeOnEdgeMutation,
      syncDraftStateMutation: realSyncDraftStateMutation,
    },
  });

  // Validate workflowId in URL
  const workflowsQ = useQuery({
    queryKey: ['workflows', currentOrgId],
    queryFn: fetchWorkflowValidationList,
    enabled: !!currentOrgId,
  });
  const workflowsRefetch = workflowsQ.refetch;

  React.useEffect(() => {
    if (!workflowId || !workflowsQ.isFetched || workflowsQ.isFetching || !workflowsQ.data) return;
    // Skip when the list is empty — this is a transient state (e.g., during query invalidation)
    // and doesn't mean the workflow was deleted.
    if (workflowsQ.data.length === 0) return;
    const exists = workflowsQ.data.some((wf) => wf.id === workflowId);
    if (!exists) goToFlows({ workflowId: null }, { replace: true });
  }, [workflowId, workflowsQ.isFetched, workflowsQ.isFetching, workflowsQ.data, goToFlows]);

  // Naming
  const workflowNaming = useWorkflowNaming({
    workflowId,
    workflowName,
    isWorkflowLoading,
    generatedName,
    setGeneratedName,
    updateWorkflowMutation,
  });

  // Node config callback
  const handleNodeConfig = React.useCallback(
    async (node: WorkflowNode) => {
      if (!isPreviewingHistory) await autoSaveIfDirty();
      setSelectedNodeForConfig(node);
      setSelectedNodeIds(new Set([node.id]));
      selectSidebarTab('config');
    },
    [selectSidebarTab, autoSaveIfDirty, isPreviewingHistory]
  );

  // Scroll the canvas to a node referenced by a comment chip, without leaving
  // the Comments tab (deliberately no selectSidebarTab('config') call — see
  // FlowDesignerCanvas's onNodeNavigate for the same re-focus trick used from
  // the playbook overlay). No-ops if the node no longer exists.
  const focusNodeFromComment = React.useCallback(
    (nodeId: string) => {
      if (!getNodes().some((n) => n.id === nodeId)) return;
      setSelectedNodeForConfig(null);
      requestAnimationFrame(() => {
        const node = getNodes().find((n) => n.id === nodeId);
        if (!node) return;
        setSelectedNodeForConfig(node);
        setSelectedNodeIds(new Set([nodeId]));
      });
    },
    [getNodes, setSelectedNodeForConfig, setSelectedNodeIds]
  );

  const handleCanvasNodeClick = React.useCallback(
    (event: React.MouseEvent, node: WorkflowNode) => {
      if (!event.metaKey && !event.ctrlKey && !event.shiftKey) handleNodeConfig(node);
    },
    [handleNodeConfig]
  );

  const handleCanvasPaneClick = React.useCallback(async () => {
    await autoSaveIfDirty();
    clearEdgeSelection();
    setSelectedNodeIds(new Set());
    if (!selectedNodeForConfig && activeSidebarTab === 'config') exitConfigTab();
  }, [selectedNodeForConfig, activeSidebarTab, exitConfigTab, autoSaveIfDirty, clearEdgeSelection]);

  // Quick create handlers
  const handleQuickCreateNode = React.useCallback(
    (sourceNodeId: string, nodeType: QuickCreateTarget) => {
      if (!sourceNodeId || isTempNodeId(sourceNodeId)) return;
      const create = nodeCrud.onCreateNodeRef.current;
      if (create)
        void create(
          { type: nodeType, data: getDefaultNodeData(nodeType, workflowFlowTypeRef.current) },
          { sourceNodeId, autoSelect: true }
        );
    },
    []
  );

  const handleBranchQuickCreate = React.useCallback(
    (
      sourceNodeId: string,
      nodeType: QuickCreateTarget,
      branchType: 'condition_true' | 'condition_false'
    ) => {
      if (!sourceNodeId || isTempNodeId(sourceNodeId)) return;
      const create = nodeCrud.onCreateNodeRef.current;
      if (create)
        void create(
          { type: nodeType, data: getDefaultNodeData(nodeType, workflowFlowTypeRef.current) },
          { sourceNodeId, autoSelect: true, edgeType: branchType }
        );
    },
    []
  );

  const handleStandaloneCreateNode = React.useCallback((nodeType: string) => {
    const create = nodeCrud.onCreateNodeRef.current;
    if (create)
      void create(
        { type: nodeType, data: getDefaultNodeData(nodeType, workflowFlowTypeRef.current) },
        { autoSelect: true }
      );
  }, []);

  // Undo/redo
  const revertProposalMutation = useRevertProposal(workflowId ?? null);
  const handleAttachedUndoEffect = React.useCallback(
    (effect: AttachedEffect) => {
      if (effect.kind === 'revert-proposal') {
        revertProposalMutation.mutate(
          { proposalId: effect.proposalId },
          {
            onSuccess: () => {
              toast.default(`Restored proposal "${effect.proposalTitle}" to pending.`);
            },
            onError: () => {
              toast.error('Could not restore the proposal to pending.');
            },
          }
        );
      }
    },
    [revertProposalMutation, toast]
  );

  const undoHistory = useUndoHistory({
    getNodes,
    getEdges,
    setNodes,
    setEdges,
    syncDraftState: (state) => syncDraftStateMutation.mutateAsync(state),
    rehydrateHandlers: React.useMemo(
      () => ({
        handleNodeConfig,
        handleQuickCreateNode,
        handleBranchQuickCreate,
        handleCacheClick: (nodeId: string, event: React.MouseEvent) => {
          setCachePopover({ nodeId, position: { x: event.clientX, y: event.clientY } });
        },
        handlePlaybookClick: (nodeId?: string) => {
          if (nodeId) setPlaybookScrollToNodeId(nodeId);
          setIsPlaybookOpen(true);
        },
        enableApiSequence: hasApiCatalog,
        enableSandboxAction: hasSandboxActions,
        enableMcp: hasMcp,
      }),
      [
        handleNodeConfig,
        handleQuickCreateNode,
        handleBranchQuickCreate,
        hasApiCatalog,
        hasSandboxActions,
        hasMcp,
      ]
    ),
    onAttachedEffect: handleAttachedUndoEffect,
  });

  const handleRestoreVersion = React.useCallback(async () => {
    if (!previewingVersion || previewingVersion.type === 'current' || !workflowId) return;
    try {
      if (previewingVersion.type === 'revision') {
        await restoreRevisionMutation.mutateAsync(previewingVersion.revision);
        draftStateRef.current = null;
        setPreviewingVersion(null);
        await workflowDetailRefetch();
        undoHistory.clearHistory();
        toast.success(`Restored Revision ${previewingVersion.revision} and saved a new revision`);
        return;
      }
      if (previewingVersion.type === 'draft-snapshot') {
        await restoreDraftSnapshotMutation.mutateAsync(previewingVersion.version);
        draftStateRef.current = null;
        setPreviewingVersion(null);
        await workflowDetailRefetch();
        undoHistory.clearHistory();
        toast.success('Restored checkpoint and saved a new revision');
        return;
      }
      const restoreUrl = `${API_URL}/workflows/${workflowId}/restore/${previewingVersion.version}`;
      const response = await fetch(restoreUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to restore snapshot');
      draftStateRef.current = null;
      setPreviewingVersion(null);
      await workflowDetailRefetch();
      void qc.invalidateQueries({ queryKey: ['workflow-snapshots', workflowId] });
      void qc.invalidateQueries({ queryKey: ['workflow-revisions', workflowId] });
      void qc.invalidateQueries({ queryKey: ['workflow', 'events', workflowId] });
      undoHistory.clearHistory();
      const label = `v${previewingVersion.version}`;
      toast.success(`Restored legacy version ${label}`);
    } catch {
      toast.error(
        previewingVersion.type === 'revision'
          ? 'Failed to restore revision'
          : previewingVersion.type === 'draft-snapshot'
            ? 'Failed to restore checkpoint'
            : 'Failed to restore legacy version'
      );
    }
  }, [
    workflowId,
    previewingVersion,
    restoreRevisionMutation,
    restoreDraftSnapshotMutation,
    workflowDetailRefetch,
    qc,
    toast,
    undoHistory,
  ]);

  const { suggestMode, isSuggesting } = useSuggestModeController({
    workflowId,
    suggestingRef,
    getNodes,
    getEdges,
    setNodes,
    setEdges,
    undoHistory,
    toast,
  });

  // Undo/redo keyboard shortcuts
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'z') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;
      if (isRunning || isPreviewingHistory || isGenerating) return;
      e.preventDefault();
      if (e.shiftKey) {
        const desc = undoHistory.redo();
        if (desc) toast.success(`Redone: ${desc}`);
      } else {
        const desc = undoHistory.undo();
        if (desc) toast.success(`Undone: ${desc}`);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undoHistory, isRunning, isPreviewingHistory, isGenerating, toast]);

  // Node graph maps
  const nodeGraphMaps = useNodeGraphMaps({
    nodes,
    edges,
    selectedNodeForConfig,
    credentialPropertyIdMap,
  });

  // Ensure workflow helper
  const ensureWorkflow = React.useCallback(async () => {
    if (workflowId) return workflowId;
    const result = await createWorkflowMutation.mutateAsync({ status: 'draft' });
    return result.workflow.id;
  }, [workflowId, createWorkflowMutation]);

  // Flow Autobuild: StarterPicker stays presentational (same contract as
  // handleStandaloneCreateNode) — this owns lazily creating the draft
  // workflow, starting the pipeline, and toasting userError messages.
  const autobuildStartMutation = useAutobuildStartMutation();
  const handleAutobuildSubmit = React.useCallback(
    async (payload: AutobuildSubmitPayload) => {
      try {
        const id = await ensureWorkflow();
        await autobuildStartMutation.mutateAsync({ workflowId: id, ...payload });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to start Autobuild', {
          reportError: !isExpectedClientApiError(error),
        });
        throw error;
      }
    },
    [ensureWorkflow, autobuildStartMutation, toast]
  );

  const prepareRunConfig = React.useCallback(async () => {
    try {
      await autoSaveIfDirty();
      await ensureWorkflow();
      await workflowDetailRefetch();
      return true;
    } catch {
      toast.error('Could not prepare run configuration', { reportError: true });
      return false;
    }
  }, [autoSaveIfDirty, ensureWorkflow, workflowDetailRefetch, toast]);

  // Edge click handler (manages menu position state)
  const handleEdgeClick = React.useCallback(
    (edgeId: string, _edge: WorkflowEdge, event: React.MouseEvent) => {
      if (selectedEdgeId === edgeId) {
        setSelectedEdgeId(null);
        setEdgeMenuPosition(null);
      } else {
        setSelectedEdgeId(edgeId);
        // Keep the Insert step menu fully on-screen. The menu caps its own
        // height at the viewport (max-h + scroll), so clamp against a realistic
        // height bounded by the viewport rather than a fixed guess.
        const menuWidth = 260;
        const padding = 8;
        const menuHeight = Math.min(560, window.innerHeight - padding * 2);
        setEdgeMenuPosition({
          x: Math.max(padding, Math.min(event.clientX, window.innerWidth - menuWidth - padding)),
          y: Math.max(padding, Math.min(event.clientY, window.innerHeight - menuHeight - padding)),
        });
      }
    },
    [selectedEdgeId]
  );

  // Node CRUD
  const nodeCrud = useNodeCrud({
    workflowId,
    workflowFlowType,
    nodes,
    edges,
    getNodes,
    setNodes,
    setEdges,
    selectedNodeForConfig,
    setSelectedNodeForConfig,
    setSelectedNodeIds,
    handleNodeConfig,
    handleQuickCreateNode,
    handleBranchQuickCreate,
    hasApiCatalog,
    hasSandboxActions,
    hasMcp,
    ensureWorkflow,
    createNodeMutation,
    updateNodeMutation,
    deleteNodeMutation,
    bulkDeleteNodesMutation,
    createEdgeMutation,
    deleteEdgeMutation,
    insertNodeOnEdgeMutation,
    workflowDetailRefetch,
    toast,
    undoHistory,
    setCachePopover,
    selectedEdgeId,
    clearEdgeSelection,
    setIsInsertingNode,
    autoSaveIfDirty,
    selectSidebarTab,
    suggestingRef,
  });

  // Clipboard (copy/paste)
  const clipboard = useClipboard({
    workflowId,
    nodes,
    edges,
    getNodes,
    setNodes,
    setEdges,
    selectedNodeIds,
    selectedNodeForConfig,
    ensureWorkflow,
    createNodeMutation,
    createEdgeMutation,
    deleteEdgeMutation,
    workflowDetailRefetch,
    toast,
    undoHistory,
    handleNodeConfig,
    handleQuickCreateNode,
    handleBranchQuickCreate,
    hasApiCatalog,
    hasSandboxActions,
    hasMcp,
    setCachePopover,
    setIsPlaybookOpen,
    setPlaybookScrollToNodeId,
  });

  // Copy/paste keyboard shortcuts
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key !== 'c' && e.key !== 'v') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;
      if (isRunning || isPreviewingHistory || isGenerating) return;
      if (e.key === 'c') {
        const selection = window.getSelection();
        if (selection && selection.toString().trim().length > 0) return;
        e.preventDefault();
        void clipboard.handleCopy();
      } else {
        e.preventDefault();
        void clipboard.handlePaste();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [clipboard, isRunning, isPreviewingHistory, isGenerating]);

  // Deactivate/reactivate a node: posts to the audited node deactivate/
  // activate endpoint (config patch + workflow event in one transaction on
  // the server), updates the canvas optimistically, and re-syncs from the
  // server. Split in two: requesting the toggle only opens the reason
  // dialog; confirming it performs the actual mutation.
  const [pendingDeactivateNode, setPendingDeactivateNode] = React.useState<{
    nodeId: string;
    next: boolean;
    label: string;
  } | null>(null);
  const setNodeDeactivatedMutation = useSetNodeDeactivated(workflowId ?? '');

  // Reads nodes through `getNodes()` rather than closing over `nodes`: the node
  // menu captures this callback once at canvas hydration, when the array is
  // still empty, so a closed-over copy resolves every label to "Step <uuid>".
  const requestToggleNodeDeactivated = React.useCallback(
    (nodeId: string, next: boolean) => {
      setPendingDeactivateNode({ nodeId, next, label: nodeLabel(getNodes(), nodeId) });
    },
    [getNodes]
  );

  // Takes the pending toggle as an argument rather than reading the dialog's
  // state: the dialog closes itself on confirm, so depending on that state
  // here would race its own teardown.
  const confirmToggleNodeDeactivated = React.useCallback(
    async (pending: { nodeId: string; next: boolean; label: string }, reason: string) => {
      const { nodeId, next } = pending;
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, deactivated: next } } : n))
      );
      const trimmedReason = reason.trim();
      try {
        await setNodeDeactivatedMutation.mutateAsync({
          nodeId,
          deactivated: next,
          ...(trimmedReason ? { reason: trimmedReason } : {}),
        });
        await workflowDetailRefetch();
      } catch (error) {
        setNodes((nds) =>
          nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, deactivated: !next } } : n))
        );
        toast.error(error instanceof Error ? error.message : 'Failed to update node activation');
      }
    },
    [setNodes, setNodeDeactivatedMutation, workflowDetailRefetch, toast]
  );

  // Canvas hydration
  useCanvasHydration({
    workflowId,
    workflowDetailQ,
    previewingVersion,
    previewSnapshotQ,
    previewRevisionQ,
    previewDraftSnapshotQ,
    nodes,
    setNodes,
    setEdges,
    getNodes,
    selectedNodeForConfig,
    setSelectedNodeForConfig,
    activeSidebarTab,
    exitConfigTab,
    handleNodeConfig,
    onToggleNodeDeactivated: requestToggleNodeDeactivated,
    handleQuickCreateNode,
    handleBranchQuickCreate,
    setCachePopover,
    setIsPlaybookOpen,
    setPlaybookScrollToNodeId,
    hasApiCatalog,
    hasSandboxActions,
    hasMcp,
    undoHistory,
    autoFocusedWorkflowRef,
    setPreviewingVersion,
    draftStateRef,
    isSuggesting,
  });

  const playbookNodeIds = React.useMemo(() => nodes.map((node) => node.id), [nodes]);
  const playbookCache = usePlaybookCache({
    workflowId: workflowId ?? '',
    nodeIds: workflowId && !previewingVersion && !isSuggesting ? playbookNodeIds : [],
  });
  const lastExecutionEventType = events.at(-1)?.type;
  React.useEffect(() => {
    if (lastExecutionEventType === 'complete' || lastExecutionEventType === 'error') {
      playbookCache.refetch();
    }
  }, [lastExecutionEventType, playbookCache.refetch]);
  React.useEffect(() => {
    playbookCache.refetch();
  }, [workflowData?.updatedAt, playbookCache.refetch]);
  React.useEffect(() => {
    if (optimizerStream.summary) playbookCache.refetch();
  }, [optimizerStream.summary?.committed, optimizerStream.summary?.failed, playbookCache.refetch]);

  // Open issues persistently linked to this flow — powers each node's issue
  // pill on the canvas (see `issuesForNode`).
  const openIssuesQuery = useWorkflowOpenIssues(workflowId);
  const { actions: sandboxActions } = useAvailableSandboxActions({
    enabled: nodes.some((node) => node.type === 'sandbox_action'),
  });

  // Canvas nodes (enrichment + execution overlays)
  const { enrichedNodes, canvasNodes } = useCanvasNodes({
    nodes,
    credentials,
    credentialPropertyMap,
    seedOutputsByWorkflowId,
    templateNameById,
    workflowInputs,
    apiRequests,
    sandboxActions,
    nodeStatuses,
    optimizerStatuses: optimizerStream.nodeStatuses,
    effectivePlaybooks:
      previewingVersion || isSuggesting ? undefined : playbookCache.effectiveByNodeId,
    currentStepId,
    historicalFailedNodeId,
    flowValidation,
    events,
    setNodes,
    openIssues: openIssuesQuery.data ?? [],
  });

  // Run execution
  const workflowEnvironments = (workflowData?.environments ?? []) as Array<{
    id: string;
    name: string;
    type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
    isDefault: boolean;
    environmentKindId?: string | null;
    environmentKindName?: string | null;
  }>;
  const workflowEligibleEnvironmentIds = (workflowData?.eligibleEnvironmentIds ?? []) as string[];
  // Server-derived env to pre-select in the run dialog (login credential's
  // bound env, when eligible). Null ⇒ dialog falls back to the property default.
  const workflowPreferredEnvironmentId = (workflowData?.preferredEnvironmentId ?? null) as
    | string
    | null;

  // Sandbox environment auto-warm: when the run target is a `type='sandbox'`
  // environment, ensure its backing instance on open and surface readiness via
  // the toolbar button.
  const environmentsPropertyId = (workflowData?.environmentsPropertyId ?? null) as string | null;
  const workflowDefaultEnvironmentId = (workflowData?.defaultEnvironmentId ?? null) as
    | string
    | null;
  const sandboxTargetEnv = React.useMemo(
    () =>
      resolveSandboxWarmTarget(
        workflowEnvironments,
        workflowEligibleEnvironmentIds,
        workflowDefaultEnvironmentId
      ),
    [workflowEnvironments, workflowEligibleEnvironmentIds, workflowDefaultEnvironmentId]
  );
  // `workflowEnvironments` (from the workflow-detail response) carries only
  // `sandboxTemplateId` (v1) — not the sandboxV2* columns — so a separate
  // lookup resolves whether the target is Sandboxes v2-backed. See
  // `useSandboxV2DesignerTarget`'s doc comment.
  const sandboxV2Target = useSandboxV2DesignerTarget(
    environmentsPropertyId,
    sandboxTargetEnv?.id ?? null,
    !!sandboxTargetEnv
  );
  // The active/most recent run's own claimed copy (phases, status), for the
  // v2 toolbar popover. Shares its query cache with the Runs tab's own
  // `useWorkflowRun(workflowId, runId)` call — this doesn't add a fetch. A
  // no-op (disabled) when no run has started yet.
  const currentRunQ = useWorkflowRun(workflowId, runId);
  const currentRunSandboxV2 = sandboxV2Target.isV2
    ? (currentRunQ.data?.data?.run?.sandboxV2Sandbox ?? null)
    : null;
  // Held off until `sandboxV2Target` resolves whether the target is v2 — v1's
  // `autoEnsure` fires its `/sandbox/ensure` POST on activation, which 400s
  // ("not a sandbox environment", a visible toast) against a v2 row, so it
  // must never fire optimistically while that's still unknown.
  const sandboxEnvironment = useSandboxEnvironment(
    environmentsPropertyId,
    sandboxTargetEnv?.id ?? null,
    {
      enabled: !!sandboxTargetEnv && !sandboxV2Target.isLoading && !sandboxV2Target.isV2,
      autoEnsure: true,
    }
  );

  const runExecution = useRunExecution({
    workflowId,
    workflowName,
    nodes,
    ensureWorkflow,
    prepareRunConfig,
    beforeStartExecution: async () => {
      if (!workflowId) return;
      try {
        const response = await fetch(`${API_URL}/workflows/${workflowId}/cache-status`, {
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Effective playbook preview failed');
        playbookCache.refetch();
      } catch {
        toast.warning(
          'Could not refresh the effective playbook preview. The run will resolve it on the server.'
        );
      }
    },
    selectSidebarTab,
    startExecution,
    createWorkflowMutationIsPending: createWorkflowMutation.isPending,
    flowValidation,
    setGeneratedName,
    setIsGeneratingName,
    workflowInputs,
    workflowScenarios,
    environments: workflowEnvironments,
    eligibleEnvironmentIds: workflowEligibleEnvironmentIds,
  });

  // Lifecycle controls. Flag ON: approval milestone + derived eligibility + pause
  // toggle. Flag OFF: approval + promotion (promote / force-promote / pause).
  const {
    runState,
    lifecycleState,
    runButtonStates,
    lifecycleButtonStates,
    isRunDisabled,
    isLifecycleDisabled,
    handleRunPress,
    handleLifecyclePress,
    handleApprove,
    handlePromote,
    handleForcePromote,
    handlePause,
    handleUnpause,
  } = useLifecycleControls({
    workflowId,
    isRunning,
    isPrevalidating: runExecution.isPrevalidating,
    eligibilityModel,
    isApproved: workflowIsApproved,
    isApprovable: workflowIsApprovable,
    firstPassedAt: workflowFirstPassedAt,
    isPaused: workflowPaused,
    promotion: workflowPromotion,
    readyToPromote: workflowReadyToPromote,
    isPromoting: promoteWorkflowMutation.isPending,
    canRunFlow: runExecution.canRunFlow,
    isApproving: approveWorkflowMutation.isPending,
    runFlow: () => runExecution.handleRunFlow({ triggerNameGeneration: true }),
    stopRun: stopExecution,
    approveFlow: async () => {
      await approveWorkflowMutation.mutateAsync();
    },
    promoteFlow: async (options?: { force?: boolean }) => {
      await promoteWorkflowMutation.mutateAsync(options);
    },
    pauseFlow: async () => {
      await pauseWorkflowMutation.mutateAsync();
    },
    unpauseFlow: async () => {
      await unpauseWorkflowMutation.mutateAsync();
    },
    onApprovePress: () => setApproveDialogOpen(true),
    onRunCompleted: () => {
      void workflowDetailRefetch();
      void workflowsRefetch();
    },
    onLifecycleChanged: async () => {
      await workflowDetailRefetch();
      await workflowsRefetch();
    },
    toast,
  });

  // Optional, on-demand AI flow review (triggered from the approve modal).
  const { requestReview } = useReviewWorkflow(workflowId);

  if (workflowNotFound) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <div className="text-center space-y-3">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Flow not found</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            This flow may have been deleted, or it belongs to a different organization.
          </p>
          <button
            onClick={() => navigate({ to: '/flows' })}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
          >
            Back to flows
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <DocsAnnotation slug="reference/flows" title="Flow Designer">
        <WorkflowVariablesProvider value={workflowVariables}>
          <FlowOverlayPortalProvider value={overlayHost}>
            <div className="flex flex-1 min-h-0">
              {/* Main canvas area — also the portal host for slide-up overlays that
              are mounted from inside the right sidebar (see
              FlowOverlayPortalContext). */}
              <div ref={setOverlayHost} className="relative flex flex-col overflow-hidden flex-1">
                {!reviewingProposalId && (
                  <FlowDesignerProposals
                    workflowId={workflowId}
                    onReview={setReviewingProposalId}
                  />
                )}
                {reviewingProposalId && workflowId ? (
                  <ProposalReviewView
                    workflowId={workflowId}
                    proposalId={reviewingProposalId}
                    currentNodes={nodes}
                    currentEdges={edges}
                    onExit={exitProposalReview}
                    captureUndoSnapshot={undoHistory.captureSnapshot}
                  />
                ) : (
                  <FlowDesignerCanvas
                    canvasNodes={canvasNodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    isPreviewingHistory={isPreviewingHistory}
                    handleCanvasNodeClick={handleCanvasNodeClick}
                    handleCanvasPaneClick={handleCanvasPaneClick}
                    handleVerticalCanvasNodeDelete={nodeCrud.handleVerticalCanvasNodeDelete}
                    handleEdgeClick={handleEdgeClick}
                    handleConnect={nodeCrud.handleConnect}
                    handlePrependNode={nodeCrud.handlePrependNode}
                    handleNodeReorder={nodeCrud.handleNodeReorder}
                    selectedNodeIds={selectedNodeIds}
                    handleSelectionChange={nodeCrud.handleSelectionChange}
                    handleMultiNodeDelete={nodeCrud.handleMultiNodeDelete}
                    handleMultiNodeReorder={nodeCrud.handleMultiNodeReorder}
                    handleOrphanInsertOnEdge={nodeCrud.handleOrphanInsertOnEdge}
                    selectedEdgeId={selectedEdgeId}
                    currentStepId={currentStepId}
                    selectedNodeForConfig={selectedNodeForConfig}
                    workflowStatus={workflowStatus}
                    workflowHealth={workflowHealth}
                    onReEnable={() => reEnableMutation.mutate()}
                    isReEnabling={reEnableMutation.isPending}
                    isGenerating={isGenerating}
                    workflowId={workflowId}
                    workflowCanvasTitle={workflowNaming.workflowCanvasTitle}
                    workflowFlowType={workflowFlowType}
                    generatedName={generatedName}
                    typingComplete={workflowNaming.typingComplete}
                    isWorkflowLoading={isWorkflowLoading}
                    isSettingsOpen={isSettingsOpen}
                    setIsSettingsOpen={setIsSettingsOpen}
                    sandboxControl={
                      sandboxTargetEnv && !sandboxV2Target.isV2 && sandboxEnvironment.sandbox
                        ? {
                            environmentName: sandboxTargetEnv.name,
                            sandbox: sandboxEnvironment.sandbox,
                            isEnsuring: sandboxEnvironment.isEnsuring,
                            isStopping: sandboxEnvironment.isStopping,
                            onStart: sandboxEnvironment.start,
                            onStop: sandboxEnvironment.stop,
                          }
                        : null
                    }
                    sandboxV2Control={
                      sandboxTargetEnv && sandboxV2Target.isV2
                        ? {
                            environmentName: sandboxTargetEnv.name,
                            environment: sandboxV2Target.environment,
                            isLoading: sandboxV2Target.isLoading,
                            status: sandboxV2Target.status,
                            version: sandboxV2Target.version,
                            error: sandboxV2Target.error,
                            currentRun: currentRunSandboxV2,
                          }
                        : null
                    }
                    isScenarioConfigOpen={isScenarioConfigOpen}
                    setIsScenarioConfigOpen={setIsScenarioConfigOpen}
                    workflowInputs={workflowInputs}
                    workflowScenarios={workflowScenarios}
                    credentialTemplates={orgTemplatesQ.data ?? []}
                    onSaveInputs={(inputs) => saveInputsMutation.mutate(inputs)}
                    isSavingInputs={saveInputsMutation.isPending}
                    onSaveScenarios={(scenarios) => saveScenariosMutation.mutate(scenarios)}
                    isSavingScenarios={saveScenariosMutation.isPending}
                    isPlaybookOpen={isPlaybookOpen}
                    setIsPlaybookOpen={setIsPlaybookOpen}
                    playbookScrollToNodeId={playbookScrollToNodeId}
                    setPlaybookScrollToNodeId={setPlaybookScrollToNodeId}
                    workflowName={workflowName}
                    workflowDescription={workflowDescription}
                    workflowPreflight={workflowPreflight}
                    workflowCleanup={workflowCleanup}
                    workflowRunAgentically={workflowRunAgentically}
                    workflowSeedRunOncePerSuite={workflowSeedRunOncePerSuite}
                    workflowCurrentStatus={workflowStatus}
                    workflowCreatedBy={workflowData?.createdBy ?? null}
                    workflowApprovedBy={workflowData?.approvedBy ?? null}
                    workflowApprovedAt={workflowData?.approvedAt ?? null}
                    workflowSource={
                      workflowData?.sourceProvider
                        ? {
                            provider: workflowData.sourceProvider,
                            externalKey: workflowData.sourceExternalKey ?? null,
                            url: workflowData.sourceUrl ?? null,
                          }
                        : null
                    }
                    workflowEnvironmentConstraints={workflowData?.environmentConstraints ?? null}
                    workflowEligibleEnvironmentIds={workflowData?.eligibleEnvironmentIds ?? null}
                    workflowEnvironments={
                      (workflowData?.environments as
                        | Array<{
                            id: string;
                            name: string;
                            type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
                            isDefault: boolean;
                          }>
                        | undefined) ?? null
                    }
                    workflowDefaultEnvironmentId={workflowData?.defaultEnvironmentId ?? null}
                    workflowPassByEnvironment={workflowData?.passByEnvironment ?? null}
                    runState={runState}
                    lifecycleState={lifecycleState}
                    runButtonStates={runButtonStates}
                    lifecycleButtonStates={lifecycleButtonStates}
                    isRunDisabled={isRunDisabled || isSuggesting}
                    isLifecycleDisabled={isLifecycleDisabled || isSuggesting}
                    handleRunPress={() => {
                      void handleRunPress();
                    }}
                    handleLifecyclePress={() => {
                      void handleLifecyclePress();
                    }}
                    handleRunWithConfig={() => {
                      void runExecution.handleRunWithConfig({ triggerNameGeneration: true });
                    }}
                    hasRunConfig={runExecution.hasRunConfig}
                    eligibilityModel={eligibilityModel}
                    workflowPaused={workflowPaused}
                    handlePause={() => {
                      void handlePause();
                    }}
                    handleUnpause={() => {
                      void handleUnpause();
                    }}
                    workflowIsApproved={workflowIsApproved}
                    workflowPromotion={workflowPromotion}
                    setForcePromoteDialogOpen={setForcePromoteDialogOpen}
                    isSuggesting={isSuggesting}
                    canSuggest={!!workflowId && !isRunning && !isPreviewingHistory && !isGenerating}
                    isSubmittingSuggestion={suggestMode.isSubmitting}
                    onEnterSuggest={suggestMode.enter}
                    onDiscardSuggest={suggestMode.discard}
                    onSubmitSuggest={suggestMode.submit}
                    forkLineage={forkLineage}
                    onOptimizePress={() => setOptimizeDialogOpen(true)}
                    isOptimizePending={isConvergencePending}
                    cachePinnedAt={cachePinnedAt}
                    effectivePinned={effectivePinned}
                    isShadowFork={isShadowFork}
                    onPinToggle={(pin) => {
                      void handlePinToggle(pin);
                    }}
                    isPinMutationPending={cachePinMutation.isPending}
                    optimization={activeOptimization}
                    nodes={nodes}
                    isInsertingNode={isInsertingNode}
                    handleStandaloneCreateNode={handleStandaloneCreateNode}
                    createWorkflowMutationIsPending={createWorkflowMutation.isPending}
                    hasApiCatalog={hasApiCatalog}
                    hasSandboxActions={hasSandboxActions}
                    hasMcp={hasMcp}
                    autobuildEnabled={autobuildEnabled}
                    onAutobuildSubmit={handleAutobuildSubmit}
                    isAutobuildSubmitting={autobuildStartMutation.isPending}
                    edgeMenuPosition={edgeMenuPosition}
                    clearEdgeSelection={clearEdgeSelection}
                    handleInsertNodeOnEdge={nodeCrud.handleInsertNodeOnEdge}
                    handleDeleteSelectedEdge={nodeCrud.handleDeleteSelectedEdge}
                    workflowData={workflowData}
                    setNodes={setNodes}
                    workflowDetailRefetch={workflowDetailRefetch}
                    playbookCache={playbookCache}
                    hintData={hintData}
                    previewingVersion={previewingVersion}
                    previewSnapshotQ={previewSnapshotQ}
                    previewRevisionQ={previewRevisionQ}
                    previewDraftSnapshotQ={previewDraftSnapshotQ}
                    handleRestoreVersion={handleRestoreVersion}
                    handleBackToCurrent={handleBackToCurrent}
                    explorationBanner={explorationBanner}
                    flowValidation={flowValidation}
                    setSelectedNodeForConfig={setSelectedNodeForConfig}
                    selectSidebarTab={selectSidebarTab}
                    snapshots={snapshotsQ.data?.snapshots ?? []}
                    revisions={revisionsQ.data ?? []}
                    draftSnapshots={draftSnapshots}
                    handleVersionSelect={handleVersionSelect}
                    onSaveVersion={handleSaveVersion}
                    isSaving={isSaving}
                    flowView={flowView}
                    setFlowView={setFlowView}
                    hasSuccessfulRun={!!workflowData?.lastRunId}
                    hasApiSequences={true}
                    workflowApiOnly={localApiOnly}
                    workflowPropertyId={derivedWorkflowPropertyId}
                    onApiOnlyToggle={handleApiOnlyToggle}
                    cacheDebugRunId={cacheDebugRunId}
                    onCloseCacheDebug={() => setCacheDebugRunId(null)}
                  />
                )}
              </div>

              {/* Right sidebar */}
              <FlowDesignerSidebar
                activeSidebarTab={activeSidebarTab}
                selectSidebarTab={selectSidebarTab}
                readOnly={isPreviewingHistory}
                selectedNodeIds={selectedNodeIds}
                nodes={nodes}
                enrichedNodes={enrichedNodes}
                selectedNodeForConfig={selectedNodeForConfig}
                selectedNodePageContext={nodeGraphMaps.selectedNodePageContext}
                selectedNodePropertyId={nodeGraphMaps.selectedNodePropertyId}
                upstreamSeedWorkflowId={nodeGraphMaps.selectedNodeUpstreamSeedWorkflowId}
                workflowId={workflowId}
                workflowPropertyId={derivedWorkflowPropertyId}
                runsPropertyId={environmentsPropertyId}
                nodeConfigPanelRef={nodeConfigPanelRef}
                onUpdateNode={nodeCrud.onUpdateNode}
                handleCloseConfigPanel={handleCloseConfigPanel}
                onDeselectNode={handleDeselectNode}
                handleMultiNodeDelete={nodeCrud.handleMultiNodeDelete}
                onCopyNodes={clipboard.handleCopy}
                flowValidation={flowValidation}
                live={{
                  isRunning,
                  isReconnecting,
                  runId,
                  events,
                  currentStepId,
                  screenshotItems,
                  reconnectToRun,
                }}
                builder={{
                  isGenerating,
                  streamId: builderStreamId,
                  nodeCount: builderNodeCount,
                  onCancel: () => refetchBuilderStatus(),
                }}
                setHistoricalFailedNodeId={setHistoricalFailedNodeId}
                onNodeSelect={(nodeId: string) => {
                  const node = nodes.find((n) => n.id === nodeId);
                  if (node) handleNodeConfig(node);
                }}
                flowView={flowView}
                onOpenCacheDebug={(runId: string) => setCacheDebugRunId(runId)}
                workflowInputs={workflowInputs}
                workflowFlowType={workflowFlowType}
                onSaveInputs={(inputs) => saveInputsMutation.mutate(inputs)}
                isSavingInputs={saveInputsMutation.isPending}
                workflowScenarios={workflowScenarios}
                onSaveScenarios={(scenarios) => saveScenariosMutation.mutate(scenarios)}
                isSavingScenarios={saveScenariosMutation.isPending}
                onOpenScenarioConfig={() => setIsScenarioConfigOpen(true)}
                width={sidebarResize.width}
                minWidth={sidebarResize.minWidth}
                maxWidth={sidebarResize.maxWidth}
                isResizing={sidebarResize.isResizing}
                resizeHandleProps={sidebarResize.resizeHandleProps}
                onCommentNodeClick={focusNodeFromComment}
                workflowManagementStatus={workflowManagementStatus}
                onChangeManagementStatus={handleChangeManagementStatus}
                isChangingManagementStatus={setWorkflowStatusMutation.isPending}
              />
            </div>
          </FlowOverlayPortalProvider>
        </WorkflowVariablesProvider>
      </DocsAnnotation>

      {runExecution.hasRunConfig && (
        <WorkflowRunConfigModal
          isOpen={runExecution.isRunConfigModalOpen}
          onOpenChange={runExecution.setIsRunConfigModalOpen}
          inputs={workflowInputs}
          scenarios={workflowScenarios}
          environments={workflowEnvironments}
          isCrossApplication={workflowData?.isCrossApplication === true}
          eligibleEnvironmentIds={workflowEligibleEnvironmentIds}
          preferredEnvironmentId={workflowPreferredEnvironmentId}
          workflowViewportSize={workflowViewportSizeForRun}
          sandboxReadiness={
            sandboxTargetEnv && sandboxEnvironment.sandbox
              ? {
                  environmentId: sandboxTargetEnv.id,
                  status: sandboxEnvironment.sandbox.status,
                }
              : null
          }
          credentialTemplates={orgTemplatesQ.data ?? []}
          onConfirm={runExecution.handleRunConfigConfirm}
        />
      )}

      <FlowDesignerDialogs
        isArchiveDialogOpen={isArchiveDialogOpen}
        setIsArchiveDialogOpen={setIsArchiveDialogOpen}
        workflowToArchive={workflowToArchive}
        setWorkflowToArchive={setWorkflowToArchive}
        archiveWorkflowMutation={archiveWorkflowMutation}
        wrongOrgInfo={wrongOrgInfo}
        setWrongOrgInfo={setWrongOrgInfo}
        switchOrg={switchOrg}
        isSwitchingOrg={isSwitchingOrg}
        workflowId={workflowId}
        navigate={navigate}
        isApproveDialogOpen={isApproveDialogOpen}
        setApproveDialogOpen={setApproveDialogOpen}
        handleApprove={handleApprove}
        isApproving={approveWorkflowMutation.isPending}
        eligibilityModel={eligibilityModel}
        hasPassed={workflowFirstPassedAt != null}
        handlePromote={handlePromote}
        isPromoting={promoteWorkflowMutation.isPending}
        readyToPromote={workflowReadyToPromote}
        onRequestReview={workflowId ? requestReview : undefined}
        onOpenProposal={(proposalId) => {
          setApproveDialogOpen(false);
          setReviewingProposalId(proposalId);
        }}
        isForcePromoteDialogOpen={isForcePromoteDialogOpen}
        setForcePromoteDialogOpen={setForcePromoteDialogOpen}
        handleForcePromote={handleForcePromote}
        isOptimizeDialogOpen={isOptimizeDialogOpen}
        setOptimizeDialogOpen={setOptimizeDialogOpen}
        handleConfirmOptimize={async () => {
          try {
            await convergenceMutation.mutateAsync();
            toast.success('Convergence started');
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to start convergence');
          }
        }}
        pendingBranchPaste={clipboard.pendingBranchPaste}
        onConfirmBranchPaste={clipboard.confirmBranchPaste}
        onCancelBranchPaste={clipboard.cancelBranchPaste}
        pendingDeactivateNode={pendingDeactivateNode}
        onDeactivateDialogOpenChange={(open) => {
          if (!open) setPendingDeactivateNode(null);
        }}
        onConfirmDeactivateNode={(reason) => {
          if (!pendingDeactivateNode) return;
          void confirmToggleNodeDeactivated(pendingDeactivateNode, reason);
        }}
      />
      <VersionNameDialog
        isOpen={isSaveVersionDialogOpen}
        onOpenChange={(open) => {
          if (!open) setIsSaveVersionDialogOpen(false);
        }}
        title="Save Revision"
        initialName=""
        onSubmit={handleVersionDialogSubmit}
        isPending={saveVersionMutation.isPending}
      />
    </>
  );
};
